
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Download.css";
import "./SendEmail.css";

const LOGO = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`;

const SendEmail = () => {
  const navigate = useNavigate();
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    message: "",
  });
  const [attachment, setAttachment] = useState(null);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message }
  const [isSending, setIsSending] = useState(false);

  // Reuses Download.css's page-shell overrides (full-bleed dark background,
  // scrollbar theming) — same class Download.jsx toggles, since this page
  // shares that dark theme rather than having its own copy.
  useEffect(() => {
    document.body.classList.add('download-page-active');
    return () => document.body.classList.remove('download-page-active');
  }, []);

  const handleChange = (e) => {
    setEmailData({ ...emailData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setAttachment(e.target.files[0] || null);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setStatus(null);

    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
    const formData = new FormData();
    formData.append("to", emailData.to);
    formData.append("subject", emailData.subject);
    formData.append("message", emailData.message);
    if (attachment) {
      formData.append("attachment", attachment);
    }

    try {
      const response = await axios.post(`${baseUrl}/api/email/send`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const message = response.data.message || "Email sent successfully.";
      setStatus({ type: "success", message });
      toast.success(message);
      setEmailData({ to: "", subject: "", message: "" });
      setAttachment(null);
      e.target.reset();
    } catch (error) {
      const message = "Failed to send email. Please try again.";
      setStatus({ type: "error", message });
      toast.error(message);
      console.error("Error:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="download-page modern sendemail-page">
      <nav className="dl-navbar">
        <div className="dl-navbar-inner">
          <img
            src={LOGO}
            alt="On-Screen"
            className="dl-navbar-logo"
            onClick={() => navigate('/about')}
          />
          <div className="dl-navbar-links">
            <button className="dl-navbar-link" onClick={() => navigate('/about')}>
              ← Back to On-Screen
            </button>
          </div>
        </div>
      </nav>

      <div className="sendemail-content">
        <div className="dl-container sendemail-container">
          <div className="sendemail-card">
            <h1 className="sendemail-title">Send an Email</h1>
            <p className="sendemail-subtitle">
              Have a question or need help? Send us a message and we'll get back to you.
            </p>

            <form className="sendemail-form" onSubmit={sendEmail}>
              <div className="sendemail-field">
                <label htmlFor="se-to">Recipient Email</label>
                <input
                  id="se-to"
                  type="email"
                  name="to"
                  placeholder="you@example.com"
                  value={emailData.to}
                  onChange={handleChange}
                  required
                  disabled={isSending}
                />
              </div>

              <div className="sendemail-field">
                <label htmlFor="se-subject">Subject</label>
                <input
                  id="se-subject"
                  type="text"
                  name="subject"
                  placeholder="What's this about?"
                  value={emailData.subject}
                  onChange={handleChange}
                  required
                  disabled={isSending}
                />
              </div>

              <div className="sendemail-field">
                <label htmlFor="se-message">Your Message</label>
                <textarea
                  id="se-message"
                  name="message"
                  placeholder="Write your message here..."
                  value={emailData.message}
                  onChange={handleChange}
                  rows={6}
                  required
                  disabled={isSending}
                />
              </div>

              <div className="sendemail-field">
                <label htmlFor="se-attachment">Attachment (optional)</label>
                <input
                  id="se-attachment"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isSending}
                />
                {attachment && <span className="sendemail-file-name">{attachment.name}</span>}
              </div>

              <button
                type="submit"
                className="cta-button primary large sendemail-submit"
                disabled={isSending}
              >
                {isSending ? "Sending…" : "Send Email"}
              </button>
            </form>

            {status && (
              <p className={`sendemail-status sendemail-status-${status.type}`}>
                {status.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmail;
